import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1>{`Media queries`}</h1>
    <p>{`The `}<inlineCode parentName="p">{`orbit-components`}</inlineCode>{` package contains several media queries that are based on a mobile-first approach.`}</p>
    <h2>{`Media query functions`}</h2>
    <p>{`By default, you should first define styles for mobile and then use queries for bigger devices.`}</p>
    <p>{`The example below is a good start to implementing media query functions in a project using `}<inlineCode parentName="p">{`styled-components`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import media from "@kiwicom/orbit-components/lib/utils/mediaQuery";
import styled, { css } from "styled-components";

const StyledComponent = styled.div\`
  width: 100%;

  \${media.desktop(css\`
    width: 50%;
  \`)};
\`;
`}</code></pre>
    <p>{`To use the component, combine it with your theme:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import media from "@kiwicom/orbit-components/lib/utils/mediaQuery";
import styled, { css } from "styled-components";
import OrbitProvider from "@kiwicom/orbit-components/lib/OrbitProvider";
import defaultTheme from "@kiwicom/orbit-components/lib/defaultTheme";

const StyledComponent = styled.div\`
  width: 100%;

  \${media.desktop(css\`
    width: 50%;
  \`)};
\`;
function App() {
  return (
    <OrbitProvider useId={React.useId} theme={defaultTheme}>
      <StyledComponent>This div will be styled.</StyledComponent>
    </OrbitProvider>
  );
}
`}</code></pre>
    <p>{`You can use the following media queries in your project:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Name`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Applies from width`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`mediumMobile`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`414px`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`largeMobile`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`576px`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`tablet`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`768px`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`desktop`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`992px`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`largeDesktop`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`1200px`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <h2>{`Breakpoints for testing purposes`}</h2>
    <p>{`To test your components with Enzyme, especially styles, you can also use the `}<inlineCode parentName="p">{`getBreakpointWidth`}</inlineCode>{` function.`}</p>
    <p>{`Imagine that you have a component and want to test if it contains specific styles:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const StyledComponent = styled.div\`
  width: 100%;

  \${media.desktop(css\`
    width: 50%;
  \`)};
`}</code></pre>
    <p>{`In this case, you would need to `}<inlineCode parentName="p">{`mount`}</inlineCode>{` this component and than check if it has specific styles with the `}<inlineCode parentName="p">{`toHaveStyleRule`}</inlineCode>{` function from the `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/jest-styled-components"
      }}><inlineCode parentName="a">{`jest-styled-components`}</inlineCode></a>{` package.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`getBreakpointWidth`}</inlineCode>{` function accepts the viewport name and a theme object. You can use it like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import * as React from "react";
import { render } from "@testing-library/react";
import theme from "@kiwicom/orbit-components/lib/defaultTheme";
import { getBreakpointWidth } from "@kiwicom/orbit-components/lib/utils/mediaQuery";

import StyledComponent from "./";

describe("StyledComponent", () => {
  it("should have 100% width by default", () => {
    render(<StyledComponent data-test="test" />);
    expect(screen.getByTestId("test")).toHaveStyleRule("width", "100%");
  });

  it("should have a width of 50% on a desktop viewport", () => {
    render(<StyledComponent data-test="test" />);
    expect(screen.getByTestId("test")).toHaveStyleRule("width", "50%", {
      media: getBreakpointWidth("desktop", theme),
    });
  });
});
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      